import settings from '@settings'
import cn from 'classnames'
import { paths, selectCurrentUserStatus, useAppSelector } from 'mmfintech-backend-api'
import { GlobalContext, tr } from 'mmfintech-commons'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import CreateTicketModal from '../CreateTicketModal/CreateTicketModal'
import FloatingSupportMenu from '../FloatingSupportMenu/FloatingSupportMenu'
import LoginRequiredModalContent from './LoginRequiredModalContent'

import styles from './SupportFab.module.scss'

const SupportIcon = () => (
  <svg
    width='22'
    height='22'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z' />
  </svg>
)

const EmailIcon = () => (
  <svg
    width='20'
    height='20'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <rect x='2' y='4' width='20' height='16' rx='2' />
    <path d='m22 6-10 7L2 6' />
  </svg>
)

const FaqIcon = () => (
  <svg
    width='20'
    height='20'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <circle cx='12' cy='12' r='10' />
    <path d='M9.09 9a3 3 0 0 1 5.83 1c0 2-3 2-3 4' />
    <line x1='12' y1='17' x2='12.01' y2='17' />
  </svg>
)

function SupportFab() {
  const [showTicketModal, setShowTicketModal] = useState(false)
  const [showLoginHint, setShowLoginHint] = useState(false)
  const { pathname } = useLocation()
  const history = useHistory()
  const { modalShow } = useContext(GlobalContext)

  const userStatus = useAppSelector(selectCurrentUserStatus)
  const isLoggedIn = useMemo(() => userStatus === 'LOGGED_IN', [userStatus])
  const isOnSupportPage = pathname.startsWith('/support')
  const isOnLoginPage = pathname === paths.login()

  // The hint is only valid on the exact page it was shown on — clear it on any navigation (SPA
  // route change keeps this component mounted, so it wouldn't otherwise reset on its own) so it
  // can't linger visible on an unrelated page. `pathname` is intentionally a trigger-only
  // dependency — the effect body doesn't read its value, it just needs to re-run on every
  // navigation. Removing it (as the rule below suggests) would break the reset entirely.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above
  useEffect(() => {
    setShowLoginHint(false)
  }, [pathname])

  function handleTicketSuccess(ticketId: number) {
    setShowTicketModal(false)
    history.push(paths.support.ticketDetail(ticketId))
  }

  function handleCustomerServiceClick() {
    // "Go to log in" is redundant when already on the login page itself — show an inline
    // hint in the menu instead of a modal whose only action would navigate nowhere new.
    if (isOnLoginPage) {
      setShowLoginHint(true)
      return
    }

    modalShow({
      header: tr('FRONTEND.SUPPORT.LOGIN_REQUIRED.TITLE', 'Login required'),
      content: <LoginRequiredModalContent />
    })
  }

  const items = [
    {
      icon: <EmailIcon />,
      label: tr('FRONTEND.SUPPORT.FAB.EMAIL_LABEL', 'Send us an email'),
      href: settings.customerServiceUrlForIndividuals
    },
    {
      icon: <SupportIcon />,
      label: (
        <>
          {tr('FRONTEND.SUPPORT.FAB.CUSTOMER_SERVICE_LABEL', 'Contact customer service')}
          {showLoginHint && (
            <span className={cn(styles['login-hint'])}>
              {tr(
                'FRONTEND.SUPPORT.LOGIN_REQUIRED.MESSAGE',
                'You need to log in to your account to contact customer service.'
              )}
            </span>
          )}
        </>
      ),
      onClick: handleCustomerServiceClick,
      keepOpenOnClick: isOnLoginPage
    },
    {
      icon: <FaqIcon />,
      label: tr('FRONTEND.SUPPORT.FAB.FAQ_LABEL', 'Frequently asked questions'),
      href: settings.FAQUrl,
      target: '_blank'
    }
  ]

  return (
    <>
      <FloatingSupportMenu
        isLoggedIn={isLoggedIn}
        onLoggedInClick={() => setShowTicketModal(true)}
        items={items}
        triggerIcon={<SupportIcon />}
        triggerLabel={tr('FRONTEND.SUPPORT.FAB.TITLE', 'Support')}
        hidden={isOnSupportPage}
        onOpenChange={open => {
          if (!open) setShowLoginHint(false)
        }}
      />

      <CreateTicketModal
        visible={showTicketModal}
        onSuccess={handleTicketSuccess}
        onClose={() => setShowTicketModal(false)}
      />
    </>
  )
}

export default SupportFab
